import {gql} from '@apollo/client';
import React from 'react';

import * as Analytics from 'analytics/Analytics';
import {AnalyticsMetadataForContext, AnalyticsModalType} from 'analytics/AnalyticsTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {MeasurementUnitFragment} from 'constants/MeasurementConstants';
import * as Context from 'Context';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {MeasurementType} from 'samples/MeasurementForm';

import {
  QuantityEditorModalQuery,
  QuantityEditorModalQueryVariables,
} from './__generated/QuantityEditorModalQuery';
import MeasurementEditorModal, {MeasurementEditorModalSubmitArgs} from './MeasurementEditorModal';

type ContainerOrInaccessible = QuantityEditorModalQuery['containers'][number];

type ContainerQuantityInfo = ExtractByTypename<ContainerOrInaccessible, 'ContainerNode'>;

export const QUANTITY_EDITOR_MODAL_QUERY = gql`
  query QuantityEditorModalQuery($containerIds: [StrictString!]!) @skipAudit {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        quantity {
          value
          unit {
            ...MeasurementUnit
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${MeasurementUnitFragment.fragment}
`;

export interface QuantityEditorModalProps {
  containerIds: Array<string>;
  onRequestSubmit: (args: MeasurementEditorModalSubmitArgs) => void;
  onRequestClose: () => void;
  analyticsMetadata?: AnalyticsMetadataForContext;
}

export default function QuantityEditorModal(props: QuantityEditorModalProps): JSX.Element {
  const result = useBenchlingQuery<QuantityEditorModalQuery, QuantityEditorModalQueryVariables>(
    QUANTITY_EDITOR_MODAL_QUERY,
    {variables: {containerIds: props.containerIds}}
  );
  const analyticsMetadataDict = {
    actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
    ...props.analyticsMetadata,
    modalType: AnalyticsModalType.BULK_UPDATE_QUANTITY,
  };

  let accessibleContainers: Array<ContainerQuantityInfo> = [];
  if (result.type === QueryResultType.SUCCESS) {
    accessibleContainers = result.data.containers.filter(
      (node: ContainerOrInaccessible): node is ContainerQuantityInfo => !isInaccessible(node)
    );
  }

  const handleRequestSubmit = ({
    newMeasurement,
    selectedContainerIds,
  }: MeasurementEditorModalSubmitArgs): void => {
    Context.usingAsync({analyticsMetadata: analyticsMetadataDict}, null, () => {
      props.onRequestSubmit({newMeasurement, selectedContainerIds});
    });
  };

  return (
    <MeasurementEditorModal
      queryResultType={result.type}
      label={getQuantityDisplayString(false)}
      labelPlural={getQuantityDisplayString(false, true)}
      measurements={accessibleContainers.map((container) => container.quantity)}
      measurementType={MeasurementType.QUANTITY}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleRequestSubmit}
      sampleStatusWarningParams={{
        actionAttempted: 'update_quantity',
        actionOrigin: props.analyticsMetadata?.actionOrigin ?? ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
        sampleIds: props.containerIds,
        eventGroupId: props.analyticsMetadata?.eventGroupId ?? Analytics.makeAnalyticsEventId(),
      }}
      itemIds={props.containerIds}
      analyticsMetadata={analyticsMetadataDict}
    />
  );
}
